import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DocMarkdown from '../docs/DocMarkdown';
import { Feedback } from '../docs/DocArticle';
import { parse } from '../../lib/docsMarkdown';
import { errorOf } from '../../lib/docs';
import { helpApi, day, editHref, helpHref } from '../../lib/help';
import '../docs/Docs.css';
import './Help.css';

/**
 * One Help article, to read (migration 123): the published words, "Was this
 * helpful?", and - for its editors - the figures and the way into
 * Documentation's editor. Used by the Help page (/help/:id) and, in a
 * dialog, by Create Ticket's "Try these first".
 *
 * No runbook furniture: no type, no tags, no ticks, no history - Help is
 * for reading. Everything else (links, pictures, callouts, tables) is
 * Documentation's own renderer, so an editor sees in Help exactly what
 * they wrote in Documentation.
 */

/** Text with «marks» around the words searched for, as <mark> elements. */
export function Marked({ text }) {
  const parts = String(text || '').split(/(«[^»]*»)/g);
  return parts.map((p, i) => (p.startsWith('«') ? <mark key={i}>{p.slice(1, -1)}</mark> : <React.Fragment key={i}>{p}</React.Fragment>));
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The figures an editor sees on their article. */
export function HelpStats({ stats }) {
  if (!stats) return null;
  const { views, helpful, deflections } = stats;
  return (
    <section className="hp-stats" aria-label="How this article is doing">
      <h2 className="hp-side-title">How it is doing</h2>
      <dl className="hp-stat-grid">
        <div><dt>Read</dt><dd>{plural(views.total, 'time')}</dd><span>{plural(views.people, 'person', 'people')}, {views.last30} in the last 30 days</span></div>
        <div><dt>Helpful</dt><dd>{helpful.yes} yes · {helpful.no} no</dd></div>
        <div><dt>Solved without a ticket</dt><dd data-stat="deflections">{plural(deflections.total, 'time')}</dd><span>{deflections.last30} in the last 30 days</span></div>
      </dl>
      {helpful.notes?.length > 0 && (
        <ul className="hp-notes" aria-label="What readers said">
          {helpful.notes.map((n, i) => <li key={i}>“{n.note}” <span>- {n.person_name || 'somebody'}, {day(n.updated_at)}</span></li>)}
        </ul>
      )}
      {stats.has_newer_draft && <p className="hp-hint">There are unpublished changes. Readers see the published version until somebody publishes them.</p>}
    </section>
  );
}

/**
 * The article itself, loaded by id. `onOpen` is told once it has loaded
 * (Create Ticket counts an article as opened only when it really opened).
 * `compact` is the dialog: no breadcrumb, no related list.
 */
export default function HelpReader({ id, compact = false, onOpen }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    setData(null); setErr(null);
    helpApi.get(`/help/articles/${id}`)
      .then((r) => { if (live) { setData(r); if (onOpen) onOpen(id); } })
      .catch((e) => { if (live) setErr(e?.response?.status === 404 ? 'gone' : errorOf(e)); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const blocks = useMemo(() => (data ? parse(data.article.body) : []), [data]);
  const refs = useMemo(() => (data ? { ...data.refs, helpLinks: true } : null), [data]);

  if (err === 'gone') {
    return (
      <div className="hp-gone" role="status">
        <h1 className="hp-title">This article is not available</h1>
        <p>It may have been moved or taken down. Try searching Help, or raise a ticket.</p>
        {!compact && <p><Link to="/help" className="btn btn-outline btn-small">Back to Help</Link></p>}
      </div>
    );
  }
  if (err) return <p className="dp-error" role="alert">{err}</p>;
  if (!data) return <p className="dp-hint" aria-busy="true">Opening…</p>;
  const { article } = data;
  return (
    <article className={`hp-article${compact ? ' is-compact' : ''}`} data-article={article.id}>
      {!compact && (
        <nav className="hp-crumbs" aria-label="Breadcrumb">
          <Link to="/help">Help</Link>
          {article.category && <><span aria-hidden="true"> › </span><span>{article.category.title}</span></>}
        </nav>
      )}
      <header className="hp-article-head">
        {/* In a dialog the dialog's own title names it. */}
        {!compact && <h1 className="hp-title">{article.title}</h1>}
        <p className="hp-byline">Updated {day(article.updated_at)}</p>
        {(data.can_edit || compact) && (
          <div className="hp-edit-row">
            {data.can_edit && <Link className="btn btn-outline btn-small" to={editHref(article.id)}>Edit</Link>}
            {compact && <Link className="btn btn-plain btn-small" to={helpHref(article.id)}>Open in Help</Link>}
          </div>
        )}
      </header>
      <div className={`hp-layout${compact || !data.can_edit ? '' : ' has-side'}`}>
        <div className="hp-main">
          <DocMarkdown blocks={blocks} refs={refs} className="hp-body" />
          <Feedback id={article.id} mine={data.feedback?.mine} onSaved={() => {}} />
          {!compact && (
            <p className="hp-still">
              Still stuck? <Link to="/create">Raise a ticket</Link> and IT will help.
            </p>
          )}
        </div>
        {!compact && (data.can_edit || data.related.length > 0) && (
          <aside className="hp-side">
            {data.can_edit && <HelpStats stats={data.stats} />}
            {data.related.length > 0 && (
              <section aria-label="More in this category">
                <h2 className="hp-side-title">More in {article.category ? article.category.title : 'Help'}</h2>
                <ul className="hp-links">
                  {data.related.map((r) => <li key={r.id}><Link to={helpHref(r.id)}>{r.title}</Link></li>)}
                </ul>
              </section>
            )}
          </aside>
        )}
      </div>
    </article>
  );
}
